import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
   BehaviorSubject,
   lastValueFrom,
   map,
   Observable,
   of,
   shareReplay,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import {
   MarketCoverage,
   MarketCoverageResponse,
} from '../models/market-coverage.model';
import {
   PeriodRole,
   Role,
   SubscriptionInfoModel,
} from 'src/app/models/subscription-info.model';
import { takeUntil, timeout } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CouponService } from 'src/app/shared/services/coupon.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ManageSubscriptionPopupComponent } from '../components/manage-subscription-popup/manage-subscription-popup.component';
import { DowngradePopupComponent } from '../components/downgrade-popup/downgrade-popup.component';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';

interface Feature {
   isAvailable: boolean;
   name: string;
   featureCount: number;
   featureInfo: string;
}

interface PriceLookupResponse {
   status: boolean;
   message: string;
   data: {
      id: string;
      type: string;
      monthlyPriceId: string;
      yearlyPriceId: string;
      monthlyPrice: number;
      yearlyPrice: number;
   };
}

declare global {
   interface Window {
      Plaid: any;
   }
}

@Injectable({
   providedIn: 'root',
})
export class PricingService {
   url: string = environment.apiUrl;
   roleHierarchy: PeriodRole[][] = [
      ['free'],
      ['starter_monthly', 'foundation_monthly', 'foundation', 'starter'],
      ['starter_yearly', 'foundation_yearly', 'foundation', 'starter'],
      ['premium_monthly', 'invest_monthly', 'invest', 'premium'],
      ['premium_yearly', 'invest_yearly', 'invest', 'premium'],
      ['premium_plus_monthly', 'pro_monthly', 'pro', 'premium_plus'],
      ['premium_plus_yearly', 'pro_yearly', 'pro', 'premium_plus'],
      ['professional_monthly', 'professional', 'professional'],
      ['professional_yearly', 'professional', 'professional'],
   ];
   private marketCoverageCache$!: Observable<MarketCoverage[]>;

   userNewRole: Role = null;
   currPeriod: string | null = null;
   public planDuration: string | null = null;

   private plansTabVisible: BehaviorSubject<
      'Non-Professional' | 'Professional'
   > = new BehaviorSubject<'Non-Professional' | 'Professional'>(
      'Non-Professional'
   );
   getPlansTabVisibility = this.plansTabVisible.asObservable();

   private manageSubscriptionModalRef!: NgbModalRef | null;
   private downgradeModalRef!: NgbModalRef | null;

   constructor(
      private http: HttpClient,
      private couponService: CouponService,
      private modalService: NgbModal
   ) {
      SubscriptionService.subscriptionData.subscribe(
         (data: SubscriptionInfoModel) => {
            this.currPeriod = data.period;
            this.userNewRole = data.newRole;
            this.planDuration = data.period;
         }
      );
   }

   setPlansTabVisibility(tab: 'Non-Professional' | 'Professional') {
      this.plansTabVisible.next(tab);
   }

   // fetchBrokeragesCoverage<T>(countryCode: string): Observable<T> {
   //    let params = new HttpParams().set('country_code', countryCode)

   //    return this.http.post<T>(`${this.url}/api/trading/pricing/brokerage-coverage`, { params })
   // }

   fetchBrokeragesCoverage<T>(countryCode: string): Observable<T> {
      return this.http.get<T>(
         // `${this.url}/api/trading/pricing/brokerage-coverage`
         `${this.url}/api/trading/pricing/brokerage-coverage-v2`
         // ?country_code=${countryCode}
      );
   }

   fetchInstitutions(query: string): Promise<any> {
      return this.http
         .get<any>(`${this.url}/api/plaid/search-brokerage/${query}`)
         .toPromise();
   }

   createLinkToken(): Promise<any> {
      return this.http
         .post<any>(`${this.url}/api/plaid/create_link_token/`, {})
         .toPromise();
   }

   getJwtToken(): Promise<any> {
      return this.http
         .get<any>(`${this.url}/api/featurebase/create-jwt-token/`)
         .toPromise();
   }

   getstripeSession(returnRoute: string): Promise<any> {
      return this.http
         .post<any>(`${this.url}/api/user/stripe-session/`, {
            return_url: returnRoute,
         })
         .toPromise();
   }

   calculateInvoice(
      newPriceId: string,
      changeImmediately: boolean
   ): Promise<any> {
      return this.http
         .post<any>(`${this.url}/api/user/switch-plan/`, {
            new_price_id: newPriceId,
            change_immediately: changeImmediately,
         })
         .toPromise();
   }

   plaidWebhook(params: any): Promise<any> {
      return this.http
         .post<any>(`${this.url}/api/plaid/webhook/`, params)
         .pipe(
            timeout(100000), // timeout 100 seconds (100000 ms)
            catchError((error) => {
               console.error('Request error or timeout', error);
               return throwError(() => error);
            })
         )
         .toPromise();
   }

   getMarketCoverage(): Observable<MarketCoverage[]> {
      this.marketCoverageCache$ =
         this.marketCoverageCache$ ??
         this.fetchMarketCoverage().pipe(shareReplay());
      return this.marketCoverageCache$;
   }

   private fetchMarketCoverage(): Observable<MarketCoverage[]> {
      return this.http
         .get<MarketCoverageResponse>(`${this.url}/api/portfolio/coverage`)
         .pipe(map((response) => response.data));
   }

   getBrokerageCountries(): Observable<any> {
      return this.http
         .get<any>(
            `${this.url}/api/trading/pricing/brokerage-coverage/countries`
         )
         .pipe(map((response) => response.data));
   }

   // fetchMarketCoverage<T>(): Observable<T> {
   //    return this.http.get<T>(`${this.url}/api/portfolio/coverage`)
   // }

   isSameTier(role1: PeriodRole, role2: PeriodRole): boolean {
      if (role1 === null || role2 === null) return false;
      return (
         this.roleHierarchy.findIndex((hierarchy) =>
            hierarchy.some((role) => role1 === role)
         ) ===
         this.roleHierarchy.findIndex((hierarchy) =>
            hierarchy.some((role) => role2 === role)
         )
      );
   }

   isHigherTier(role1: PeriodRole, role2: PeriodRole): boolean {
      if (role1 === null || role2 === null) return false;
      console.log(role1, role2, this.roleHierarchy.findIndex((hierarchy) =>
         hierarchy.some((role) => role1 === role)
      ), this.roleHierarchy.findIndex((hierarchy) =>
         hierarchy.some((role) => role2 === role)
      ));
      return (
         this.roleHierarchy.findIndex((hierarchy) =>
            hierarchy.some((role) => role1 === role)
         ) >
         this.roleHierarchy.findIndex((hierarchy) =>
            hierarchy.some((role) => role2 === role)
         )
      );
   }

   isLowerTier(role1: PeriodRole, role2: PeriodRole): boolean {
      if (role1 === null || role2 === null) return false;
      return (
         this.roleHierarchy.findIndex((hierarchy) =>
            hierarchy.some((role) => role1 === role)
         ) <
         this.roleHierarchy.findIndex((hierarchy) =>
            hierarchy.some((role) => role2 === role)
         )
      );
   }

   async getSubscriptionDataFromLookup(
      lookupKey: string,
      countryCode: string
   ): Promise<PriceLookupResponse> {
      const response: PriceLookupResponse = await lastValueFrom(
         this.http.get<PriceLookupResponse>(
            `${this.url}/api/v2/plans/stripe-price/${lookupKey}?country=${countryCode}`
         )
      );

      if (
         response.status &&
         response.message === 'Price information retrieved successfully'
      ) {
         return response;
      }

      throw new Error('Failed to retrieve price information');
   }

   async getSubscriptionPlansData(countryCode: string | null) {
      const coupon = this.couponService.getStoredCoupon();
      const response: any = await lastValueFrom(
         this.http.get(`${this.url}/api/v2/plans?country=${countryCode ?? 'US'}&coupon=${coupon}`)
      );
      const data = response.data;

      const freePlan = {
         id: 'free',
         type: 'Free',
         monthlyPriceId: '',
         yearlyPriceId: '',
         monthlyPrice: 0,
         yearlyPrice: 0,
         currency: data.starter?.year.currency.toUpperCase(),
         planIncludes: [],
         monthlyCouponPercentOff: 0,
         yearlyCouponPercentOff: 0,
      };

      const starterPlan = {
         id: 'starter',
         type: 'Starter',
         monthlyPriceId: data.starter?.month.id,
         yearlyPriceId: data.starter?.year.id,
         monthlyPrice: data.starter?.month.price,
         yearlyPrice: data.starter?.year.price,
         monthlyUsdPrice: data.starter?.month.usd_price,
         yearlyUsdPrice: data.starter?.year.usd_price,
         currency: data.starter?.year.currency.toUpperCase(),
         planIncludes: ['Screener Basic'],
         monthlyCouponPercentOff:
            data.starter?.month?.coupon_applied?.percent_off,
         yearlyCouponPercentOff:
            data.starter?.year?.coupon_applied?.percent_off,
      };

      const premiumPlan = {
         id: 'premium',
         type: 'Premium',
         monthlyPriceId: data.premium?.month.id,
         yearlyPriceId: data.premium?.year.id,
         monthlyPrice: data.premium?.month.price,
         yearlyPrice: data.premium?.year.price,
         monthlyUsdPrice: data.premium?.month.usd_price,
         yearlyUsdPrice: data.premium?.year.usd_price,
         currency: data.premium?.year.currency.toUpperCase(),
         planIncludes: ['Screener', 'Portfolio'],
         monthlyCouponPercentOff:
            data.premium?.month?.coupon_applied?.percent_off,
         yearlyCouponPercentOff:
            data.premium?.year?.coupon_applied?.percent_off,
      };

      const premiumPlusPlan = {
         id: 'premium_plus',
         type: 'Premium Plus',
         monthlyPriceId: data.premium_plus?.month.id,
         yearlyPriceId: data.premium_plus?.year.id,
         monthlyPrice: data.premium_plus?.month.price,
         yearlyPrice: data.premium_plus?.year.price,
         monthlyUsdPrice: data.premium_plus?.month.usd_price,
         yearlyUsdPrice: data.premium_plus?.year.usd_price,
         currency: data.premium_plus?.year.currency.toUpperCase(),
         planIncludes: ['Screener', 'Portfolio', 'Trade'],
         monthlyCouponPercentOff:
            data.premium_plus?.month?.coupon_applied?.percent_off,
         yearlyCouponPercentOff:
            data.premium_plus?.year?.coupon_applied?.percent_off,
      };

      const professionalPlan = {
         id: 'professional',
         type: 'Professional',
         monthlyPriceId: data.professional?.month.id,
         yearlyPriceId: data.professional?.year.id,
         monthlyPrice: data.professional?.month.price,
         yearlyPrice: data.professional?.year.price,
         monthlyUsdPrice: data.professional?.month.usd_price,
         yearlyUsdPrice: data.professional?.year.usd_price,
         currency: data.professional?.year.currency.toUpperCase(),
         planIncludes: ['For Professional Use'],
         monthlyCouponPercentOff:
            data.professional?.month?.coupon_applied?.percent_off,
         yearlyCouponPercentOff:
            data.professional?.year?.coupon_applied?.percent_off,
      };

      return {
         freePlan,
         starterPlan,
         premiumPlan,
         premiumPlusPlan,
         professionalPlan,
      };
   }

   private subscriptionPlanCache: Map<string, any> = new Map();
   private inFlightRequests: Map<string, Observable<any>> = new Map();

   clearPlanCache(): void {
      this.subscriptionPlanCache.clear();
   }

   public getSubscriptionPlanV3Cache(countryCode: string = 'US') {
      return this.subscriptionPlanCache.get(countryCode.toUpperCase());
   }

   async getSubscriptionPlanV3(countryCode: string = 'US') {
      // Use uppercase for consistency
      const code = countryCode.toUpperCase();
      
      // 1. Return from completed cache if available
      if (this.subscriptionPlanCache.has(code)) {
         return this.subscriptionPlanCache.get(code);
      }

      // 2. Return the existing in-flight request if one is already active
      if (this.inFlightRequests.has(code)) {
         return lastValueFrom(this.inFlightRequests.get(code)!);
      }

      // 3. Start a new request and share it
      const obs$ = this.http.get<any>(`${this.url}/api/v3/plans?country=${code}`).pipe(
         map(response => response.data),
         shareReplay(1) // Cache the result for any simultaneous subscribers
      );

      this.inFlightRequests.set(code, obs$);

      try {
         const data = await lastValueFrom(obs$);
         this.subscriptionPlanCache.set(code, data);
         return data;
      } finally {
         // Cleanup tracking once the request finishes (success or error)
         this.inFlightRequests.delete(code);
      }
   }



   public getPlanAction(
      plan: any,
      checkPeriod: boolean = true
   ): 'upgrade' | 'downgrade' | 'selected' | null {
      let actionButton: 'upgrade' | 'downgrade' | 'selected' | null = null;

      if (plan) {
         let userPlan: PeriodRole = (this.userNewRole +
            '_' +
            this.currPeriod) as PeriodRole;
         let planId: PeriodRole = plan.key as PeriodRole;
         if (!checkPeriod) {
            userPlan = (userPlan?.split('_').slice(0, -1).join('_') ?? '') as PeriodRole;
            planId = (planId?.split('_').slice(0, -1).join('_') ?? '') as PeriodRole;
         }

         if (!this.userNewRole || this.userNewRole === 'free') {
            actionButton = null;
         } else if (this.isHigherTier(userPlan, planId)) {
            actionButton = 'downgrade';
         } else if (this.isLowerTier(userPlan, planId)) {
            actionButton = 'upgrade';
         } else if (this.isSameTier(userPlan, planId)) {
            actionButton = 'selected';
         }
      }
      return actionButton;
   }

   // Modal APIs
   openManageSubscriptionModal(
      priceId: string | undefined,
      onProceed?: () => void,
      onClose?: () => void
   ): void {
      this.manageSubscriptionModalRef = this.modalService.open(
         ManageSubscriptionPopupComponent,
         { centered: true }
      );
      const instance = this.manageSubscriptionModalRef
         .componentInstance as ManageSubscriptionPopupComponent;
      instance.priceId = priceId;
      instance.proceedToPayment.subscribe(() => {
         if (onProceed) {
            onProceed();
         }
      });
      instance.close.subscribe(() => {
         this.closeManageSubscriptionModal();
         if (onClose) {
            onClose();
         }
      });
   }

   closeManageSubscriptionModal(): void {
      if (this.manageSubscriptionModalRef) {
         this.manageSubscriptionModalRef.close();
         this.manageSubscriptionModalRef = null;
      }
   }

   openDowngradeModal(): void {
      this.downgradeModalRef = this.modalService.open(DowngradePopupComponent, {
         centered: true,
         size: 'md',
      });
      const instance = this.downgradeModalRef
         .componentInstance as DowngradePopupComponent;
      instance.close.subscribe(() => this.closeDowngradeModal());
   }

   closeDowngradeModal(): void {
      if (this.downgradeModalRef) {
         this.downgradeModalRef.close();
         this.downgradeModalRef = null;
      }
   }
}
